import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #ironsideVariants;
  #ironsidePendingVariant;
  #ironsideRenderFrame;
  #ironsideRenderedVariantId;

  connectedCallback() {
    super.connectedCallback();
    this.#ironsideRenderedVariantId = this.dataset.ironsidePriceVariantId ?? '';
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#ironsideRenderFrame) cancelAnimationFrame(this.#ironsideRenderFrame);
    this.#ironsideRenderFrame = undefined;
    this.#ironsidePendingVariant = undefined;
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      this.#ironsideVariants = undefined;
      this.#ironsideRenderedVariantId = '';
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (this.updateIronsidePrice(event.detail.resource)) return;

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Schedules a Mexican price update for the variant selected in this product context.
   * @param {{ id?: string | number } | null | undefined} resource
   * @returns {boolean}
   */
  updateIronsidePrice(resource) {
    if (!this.hasAttribute('data-ironside-price-mx-root') || !resource?.id) return false;

    const variant = this.#getIronsideVariants().find((item) => String(item.id) === String(resource.id));
    if (!variant || this.#ironsideRenderedVariantId === String(variant.id)) return true;

    this.#ironsidePendingVariant = variant;
    if (this.#ironsideRenderFrame) return true;

    this.#ironsideRenderFrame = requestAnimationFrame(() => {
      this.#ironsideRenderFrame = undefined;
      const pendingVariant = this.#ironsidePendingVariant;
      this.#ironsidePendingVariant = undefined;

      if (!pendingVariant || this.#ironsideRenderedVariantId === String(pendingVariant.id)) return;

      this.#renderIronsidePrice(pendingVariant);
      this.#updateIronsideInstallments(pendingVariant);
      this.#ironsideRenderedVariantId = String(pendingVariant.id);
      this.dataset.ironsidePriceVariantId = String(pendingVariant.id);
    });

    return true;
  }

  #getIronsideVariants() {
    if (this.#ironsideVariants) return this.#ironsideVariants;

    const data = this.querySelector('[data-ironside-price-variants]')?.textContent;
    try {
      this.#ironsideVariants = JSON.parse(data ?? '[]');
    } catch {
      this.#ironsideVariants = [];
    }

    return this.#ironsideVariants;
  }

  #renderIronsidePrice(variant) {
    const container = this.querySelector('[data-ironside-price-container]');
    if (!container) return;

    const discount = this.#getIronsideDiscount(variant);
    const saleGroup = container.querySelector('.price__sale');
    const currentPrice = container.querySelector('[data-ironside-current-price]');

    if (discount > 0) {
      if (!saleGroup) {
        container.replaceChildren(this.#createIronsideSalePrice(variant, discount));
        return;
      }

      saleGroup.querySelector('[data-ironside-price-discount]').textContent = `${discount}% OFF`;
      saleGroup.querySelector('[data-ironside-current-price]').textContent = this.#formatIronsideMoney(variant.price);
      saleGroup.querySelector('[data-ironside-compare-price]').textContent = this.#formatIronsideMoney(variant.compare_at_price);
      return;
    }

    if (saleGroup) {
      container.replaceChildren(this.#createIronsideRegularPrice(variant));
      return;
    }

    if (currentPrice) currentPrice.textContent = this.#formatIronsideMoney(variant.price);
  }

  #updateIronsideInstallments(variant) {
    const input = this.querySelector('.installments input[name="id"]');
    if (!(input instanceof HTMLInputElement) || input.value === String(variant.id)) return;

    input.value = String(variant.id);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #getIronsideDiscount(variant) {
    if (!variant.available || !variant.compare_at_price || variant.compare_at_price <= variant.price) return 0;
    return Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
  }

  #formatIronsideMoney(cents) {
    const amount = Math.round(Number(cents) || 0);
    const negative = amount < 0 ? '-' : '';
    const absoluteAmount = Math.abs(amount);
    const pesos = Math.floor(absoluteAmount / 100);
    const centavos = absoluteAmount % 100;
    const formattedPesos = pesos.toLocaleString('es-MX');

    return `${negative}$${formattedPesos}${centavos > 0 ? `.${String(centavos).padStart(2, '0')}` : ''}`;
  }

  #createIronsideSalePrice(variant, discount) {
    const group = document.createElement('span');
    group.setAttribute('role', 'group');
    group.className = 'price__sale';
    group.append(
      this.#createIronsideHiddenText('Descuento\u00a0'),
      this.#createIronsideTextElement('span', 'ironside-price-discount', `${discount}% OFF`, 'data-ironside-price-discount'),
      this.#createIronsideHiddenText('Precio de oferta\u00a0'),
      this.#createIronsideTextElement(
        'span',
        'price price-item price-item--sale price-item--last',
        this.#formatIronsideMoney(variant.price),
        'data-ironside-current-price'
      ),
      this.#createIronsideHiddenText('Precio habitual\u00a0'),
      this.#createIronsideTextElement(
        's',
        'compare-at-price price-item price-item--regular',
        this.#formatIronsideMoney(variant.compare_at_price),
        'data-ironside-compare-price'
      )
    );
    return group;
  }

  #createIronsideRegularPrice(variant) {
    return this.#createIronsideTextElement(
      'span',
      'price price-item price-item--regular',
      this.#formatIronsideMoney(variant.price),
      'data-ironside-current-price'
    );
  }

  #createIronsideHiddenText(text) {
    return this.#createIronsideTextElement('span', 'visually-hidden', text);
  }

  #createIronsideTextElement(tagName, className, text, attributeName) {
    const element = document.createElement(tagName);
    element.className = className;
    element.textContent = text;
    if (attributeName) element.setAttribute(attributeName, '');
    return element;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
